<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  show: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  initialName: { type: String, default: '' },
  currentName: { type: String, default: '' },
});

const emit = defineEmits(['close', 'submit']);
const MIN_LIST_NAME_LENGTH = 3;
const name = ref('');

const isEdit = computed(() => props.mode === 'edit');
const baselineName = computed(() =>
  isEdit.value ? props.currentName : props.initialName
);

watch(
  () => [props.show, baselineName.value],
  ([show]) => {
    if (show) {
      name.value = baselineName.value || '';
    }
  },
  { immediate: true }
);

const trimmedName = computed(() => name.value.trim());
const nameLength = computed(() => trimmedName.value.length);
const isNameTooShort = computed(
  () => nameLength.value > 0 && nameLength.value < MIN_LIST_NAME_LENGTH
);
const isNameLengthInvalid = computed(
  () => isNameTooShort.value || nameLength.value > 40
);
const unchanged = computed(
  () => trimmedName.value === (props.currentName || '').trim()
);
const canSubmit = computed(
  () =>
    (!isEdit.value ? !props.disabled : !unchanged.value) &&
    !props.isLoading &&
    nameLength.value >= MIN_LIST_NAME_LENGTH &&
    nameLength.value <= 40
);

const title = computed(() => (isEdit.value ? 'Editar lista' : 'Criar lista'));
const submitIcon = computed(() =>
  isEdit.value ? 'i-lucide-save' : 'i-lucide-folder-plus'
);
const submitLabel = computed(() => (isEdit.value ? 'Salvar' : 'Criar'));

const submit = () => {
  if (!canSubmit.value) return;

  emit('submit', trimmedName.value);
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisModal
    :show="show"
    :title="title"
    max-width-class="max-w-[520px]"
    @close="emit('close')"
  >
    <div class="space-y-4">
      <p class="text-[13px] leading-6 text-lais-text-muted">
        Organize seus imóveis favoritos em listas que você pode atualizar ao
        longo da sua busca pelo lar ideal.
      </p>

      <LaisField label="Nome da lista">
        <input
          v-model="name"
          type="text"
          maxlength="40"
          class="w-full rounded-lais border border-lais-border bg-white px-3 py-2.5 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-[#efe8fb]"
          placeholder="Exemplo: casas no centro"
          :disabled="disabled || isLoading"
          @keydown.enter.prevent="submit"
        />
      </LaisField>

      <p v-if="isNameTooShort" class="text-[12px] font-semibold text-[#b42318]">
        O nome precisa ter ao menos 3 caracteres.
      </p>

      <div class="flex items-center justify-between gap-3 text-[12px]">
        <span class="text-lais-text-muted"> Nome da lista </span>
        <span
          class="font-semibold"
          :class="
            isNameLengthInvalid ? 'text-[#b42318]' : 'text-lais-text-muted'
          "
        >
          {{ nameLength }}/40
        </span>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-wrap justify-end gap-2">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          Cancelar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :icon="submitIcon"
          :is-loading="isLoading"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ submitLabel }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
